/**
 * AI Use Case Register — PDF report builder.
 *
 * Pick the use cases and the sections, get an A4 PDF that mirrors what the detail page
 * shows. Kept as its own page rather than bolting multi-select onto RegisterItemList,
 * because that component is shared by six other registers and this is a report surface
 * rather than a list behaviour.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { aiUseCaseApi, peopleApi, usersApi, departmentsApi } from '../../services/api';
import type { AiActClassification, AiUseCase, AiType, AiUseCaseStatus } from '../../types/models';
import { AI_TYPE_LABELS, AI_STATUS_LABELS, AI_STATUS_TONE } from '../../constants/aiGovernance';
import {
  AI_ACT_CLASSIFICATIONS,
  AI_ACT_CLASSIFICATION_LABELS,
  AI_ACT_CLASSIFICATION_TONE,
} from '../../constants/aiAct';
import { exportAiUseCasesPdf, ALL_SECTIONS, type PdfSections } from '../../utils/aiUseCasePdf';
import { useToast } from '../../hooks/useToast';
import { aiUseCaseKeys } from './aiUseCaseKeys';

// Order matches the order the sections appear in the document.
const SECTION_LABELS: Array<{ key: keyof PdfSections; label: string; hint: string }> = [
  { key: 'identity',       label: 'Ownership & accountability', hint: 'Department and the five RACI roles' },
  { key: 'classification', label: 'AI Act position',            hint: 'Risk tier and why, GPAI, role, automated decisions, human oversight, vendors, transfers, monitoring, review cadence and the approval decision' },
  { key: 'businessCase',   label: 'Business case',              hint: 'Problem, value and alternatives considered' },
  { key: 'outcomes',       label: 'Expected outcomes',          hint: 'Outcomes, metrics and target dates' },
  { key: 'dataPrivacy',    label: 'Data & privacy',             hint: 'Data inventory, GDPR basis, retention, third parties' },
  { key: 'governance',     label: 'Record history',             hint: 'Status, created and last-updated timestamps' },
];

export const AiUseCaseReport: React.FC = () => {
  const toast = useToast();
  const { data: useCases = [], isLoading, error } = useQuery<AiUseCase[]>({
    queryKey: aiUseCaseKeys.collection(),
    queryFn: () => aiUseCaseApi.list(),
  });

  // Three id spaces appear across the document — `accountablePersonId` (a personId),
  // `decidedBy` (a userId) and `departmentId` — and no register can resolve another's ids.
  // All three are fetched here, where there is a query client, and passed to the PDF builder
  // as one map: that module has no data access and would otherwise have to print raw ids.
  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: usersResponse } = useQuery<{ users?: Array<{ userId?: string; name?: string; email?: string }> }>({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  });
  const { data: departments = [] } = useQuery<Array<{ departmentId?: string; name?: string }>>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const names = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    // The real People-register records carry firstName/lastName (confirmed live against the
    // DynamoDB table), not personName - trusting personName alone renders every owner blank.
    (people as Array<{ personId?: string; personName?: string; firstName?: string; lastName?: string }>).forEach((p) => {
      if (!p.personId) return;
      const label = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.personName || '';
      if (label) map[p.personId] = label;
    });
    (usersResponse?.users || []).forEach((u) => {
      if (u.userId) map[u.userId] = u.name || u.email || '';
    });
    departments.forEach((d) => {
      if (d.departmentId && d.name) map[d.departmentId] = d.name;
    });
    // A blank name would resolve as "found but empty" and print nothing at all, which is
    // worse than printing the id under an honest label.
    Object.keys(map).forEach((id) => { if (!map[id]) delete map[id]; });
    return map;
  }, [people, usersResponse, departments]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sections, setSections] = useState<PdfSections>(ALL_SECTIONS);
  const [onePerPage, setOnePerPage] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // 'any' rather than '' is the no-filter sentinel: '' is a real tier here ("not yet
  // classified"), and an AI Act export is one of the two things people come to this page for.
  const [tierFilter, setTierFilter] = useState<AiActClassification | 'any'>('any');

  const visible = useMemo(
    () => useCases.filter(uc =>
      (!statusFilter || uc.status === statusFilter) &&
      (!typeFilter || uc.aiType === typeFilter) &&
      (tierFilter === 'any' || (uc.aiActClassification || '') === tierFilter)),
    [useCases, statusFilter, typeFilter, tierFilter],
  );

  const allVisibleSelected = visible.length > 0 && visible.every(uc => selected.has(uc.useCaseId));

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach(uc => next.delete(uc.useCaseId));
      else visible.forEach(uc => next.add(uc.useCaseId));
      return next;
    });

  const chosen = useMemo(
    () => useCases.filter(uc => selected.has(uc.useCaseId)),
    [useCases, selected],
  );

  const anySection = Object.values(sections).some(Boolean);

  const handleExport = () => {
    if (!chosen.length) { toast.error('Select at least one use case to include.'); return; }
    if (!anySection)    { toast.error('Select at least one section to include.'); return; }
    try {
      exportAiUseCasesPdf(chosen, sections, { onePerPage, names });
      toast.success(`Exported ${chosen.length} use case${chosen.length === 1 ? '' : 's'} to PDF.`);
    } catch (e) {
      toast.error(`Could not generate the PDF: ${(e as Error).message}`);
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-file-pdf-2-line"
        title="AI Use Case Report"
        subtitle="Select use cases and export them as a print-ready PDF"
        colour="primary"
        actions={
          <>
            <Link to="/ai-use-cases" className="np-btn np-btn-ghost">
              <i className="ri-arrow-left-line"></i>Back to register
            </Link>
            <button
              type="button"
              className="np-btn np-btn-primary"
              onClick={handleExport}
              disabled={!chosen.length || !anySection}
            >
              <i className="ri-download-2-line"></i>
              Export PDF{chosen.length ? ` (${chosen.length})` : ''}
            </button>
          </>
        }
      />

      {error ? (
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>Could not load the register.
        </div>
      ) : isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="np-grid np-grid-8-4">
          {/* ── Selection ── */}
          <div className="np-card">
            <div className="np-card-head">
              <h2><i className="ri-list-check-2"></i>Use cases</h2>
              <span className="np-chip np-tone-neutral">
                {chosen.length} of {useCases.length} selected
              </span>
            </div>

            <div className="np-toolbar">
              <select className="np-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {(Object.keys(AI_STATUS_LABELS) as AiUseCaseStatus[]).map(s => (
                  <option key={s} value={s}>{AI_STATUS_LABELS[s]}</option>
                ))}
              </select>
              <select className="np-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All AI types</option>
                {(Object.keys(AI_TYPE_LABELS) as AiType[]).map(t => (
                  <option key={t} value={t}>{AI_TYPE_LABELS[t]}</option>
                ))}
              </select>
              <select
                className="np-select"
                value={tierFilter}
                onChange={e => setTierFilter(e.target.value as AiActClassification | 'any')}
                aria-label="Filter by AI Act classification"
              >
                <option value="any">All risk tiers</option>
                {AI_ACT_CLASSIFICATIONS.map(c => (
                  <option key={c} value={c}>{AI_ACT_CLASSIFICATION_LABELS[c]}</option>
                ))}
                <option value="">{AI_ACT_CLASSIFICATION_LABELS['']}</option>
              </select>
              <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={toggleAllVisible} disabled={!visible.length}>
                <i className={allVisibleSelected ? 'ri-checkbox-blank-line' : 'ri-checkbox-multiple-line'}></i>
                {allVisibleSelected ? 'Clear these' : 'Select these'}
              </button>
            </div>

            <div className="np-card-body p-0">
              {!visible.length ? (
                <div className="text-center py-5">
                  <i className="ri-robot-line ri-36px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
                  <p className="np-sub mb-0">
                    {useCases.length ? 'No use cases match those filters.' : 'The register is empty.'}
                  </p>
                </div>
              ) : (
                <div className="np-table-wrap">
                  <table className="np-table">
                    <thead>
                      <tr>
                        <th style={{ width: '44px' }}></th>
                        <th style={{ width: '84px' }}>ID</th>
                        <th>Use case</th>
                        <th style={{ width: '132px' }}>Type</th>
                        <th style={{ width: '128px' }}>Risk tier</th>
                        <th style={{ width: '104px' }}>Status</th>
                        <th style={{ width: '76px' }}>Fields</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(uc => {
                        const isOn = selected.has(uc.useCaseId);
                        // 'customer_controlled' counts as a real personal-data signal here too.
                        const personal = (uc.dataFields ?? []).filter(f => f.isPersonalData !== 'no').length;
                        const tier = (uc.aiActClassification || '') as AiActClassification;
                        return (
                          <tr
                            key={uc.useCaseId}
                            onClick={() => toggle(uc.useCaseId)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={isOn}
                                onChange={() => toggle(uc.useCaseId)}
                                onClick={e => e.stopPropagation()}
                                aria-label={`Include ${uc.name || uc.useCaseId}`}
                              />
                            </td>
                            <td className="np-tabular">{uc.useCaseId}</td>
                            <td className="np-row-title">{uc.name || <span className="np-hint">Untitled draft</span>}</td>
                            <td>
                              {uc.aiType
                                ? <span className="np-chip np-tone-primary">{AI_TYPE_LABELS[uc.aiType as AiType]}</span>
                                : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                            </td>
                            <td>
                              {/* Never dashed out. An empty cell would read as "no risk", and
                                  an unclassified record is the one worth exporting. */}
                              <span className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[tier]}`}>
                                {AI_ACT_CLASSIFICATION_LABELS[tier]}
                              </span>
                            </td>
                            <td>
                              <span className={`np-chip np-tone-${AI_STATUS_TONE[uc.status] ?? 'neutral'}`}>
                                {AI_STATUS_LABELS[uc.status] ?? uc.status}
                              </span>
                            </td>
                            <td className="np-hint np-tabular">
                              {(uc.dataFields ?? []).length}
                              {personal > 0 && <span className="np-chip np-tone-warning ms-1">{personal}p</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* ── Options ── */}
          <div>
            <div className="np-card mb-4">
              <div className="np-card-head">
                <h2><i className="ri-layout-row-line"></i>Sections</h2>
              </div>
              <div className="np-card-body">
                {SECTION_LABELS.map(({ key, label, hint }) => (
                  <label key={key} className="d-flex align-items-start gap-2 mb-3" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={sections[key]}
                      onChange={e => setSections(prev => ({ ...prev, [key]: e.target.checked }))}
                      style={{ marginTop: '0.2rem' }}
                    />
                    <span>
                      <span className="d-block" style={{ fontWeight: 600, color: 'var(--bs-heading-color)' }}>{label}</span>
                      <span className="np-hint">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="np-card mb-4">
              <div className="np-card-head">
                <h2><i className="ri-file-copy-2-line"></i>Layout</h2>
              </div>
              <div className="np-card-body">
                <label className="d-flex align-items-start gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={onePerPage}
                    onChange={e => setOnePerPage(e.target.checked)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    <span className="d-block" style={{ fontWeight: 600, color: 'var(--bs-heading-color)' }}>
                      Start each use case on a new page
                    </span>
                    <span className="np-hint">
                      Recommended when the report is being circulated per use case. Turn off to
                      pack them continuously.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="np-banner np-tone-info">
              <i className="ri-information-line"></i>
              <span>
                Text in the PDF is selectable and searchable, and content is never split across a
                page break — table rows and headings move whole.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiUseCaseReport;
